/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { IGateway } from "./gateway";
import { SearchPaginateType } from "./components/datatable";
import { DropDownResponse, OptionTypeBase } from "./common";
import { UntaggedConditionRuleData } from "./rule";
import { HL7ServiceMode, HL7ServiceType } from "../constant";

export interface IHL7ServiceList {
  name: string;
  gateway: IGateway;
  port: string;
  id: string;
  messageMappings: IMessageMappingList[];
  type?: string;
}

export interface IHL7ServiceSearch extends SearchPaginateType {
  status?: boolean;
  searchKeyword?: string;
  gateways?: string[];
  type?: string;
}
export interface IMessageMappingList {
  id: string;
  messageType: string;
  triggerEventsDropDown: ITriggerEventDropDown[];
}

export interface ITriggerEventDropDown {
  code: string;
  description: string;
}

export interface IMessageMappingModel {
  id?: string;
  messageType: {
    options: OptionTypeBase[];
    value?: OptionTypeBase | undefined;
  };
  triggerEvents: {
    value?: Array<OptionTypeBase> | undefined;
    disabled?: boolean;
    options: OptionTypeBase[];
  };
  dataMapping: {
    value?: OptionTypeBase | undefined;
    options: OptionTypeBase[];
    disabled?: boolean;
  };
}

export type RetentionTime = {
  value: number;
  periodType: "DAYS" | "MONTHS";
};

interface HL7ServiceGatewayResponse {
  id?: string;
  name?: string;
}

export interface WorklistStatusResponse {
  id?: string;
  name?: string;
}

export type HLServiceMessageMappingType = {
  id?: string;
  messageType?: string;
  triggerEventsDropDown?: Array<TriggerEventsResponse>;
  dataMapping?: {
    id?: string;
    name?: string;
    messageType?: string;
    versions?: Array<string>;
  };
};

export interface HL7Service {
  id?: string;
  name: string;
  gateway?: HL7ServiceGatewayResponse;
  port?: number;
  whitelistIps: string;
  enabled: boolean;
  retentionTimeSuccess?: RetentionTime;
  retentionTimeFailed?: RetentionTime;
  defaultTimezone?: string;
  defaultOrganisation?: string;
  timezone?: string;
  defaultIssuerOfPatient?: OptionTypeBase;
  defaultIssuerOfPatientId?: string;
  defaultIssuerOfAccessionNumberOption?: OptionTypeBase;
  defaultIssuerOfAccessionNumber?: string;
  conditionRule?: UntaggedConditionRuleData | null;
  transforms: any[];
  version?: string;
  migration?: boolean;
  type?: HL7ServiceType;
  countryCode?: string;
  mode?: HL7ServiceMode;
  path?: string;
  defaultWorklistStatus?: WorklistStatusResponse;
  allowReportOverride?: boolean;
  identifyOrderBy: IdentifyOrderBy;
  autoGenerateAccessionNumber?: OptionTypeBase;
  autoGenerateAccessionNumberId?: string;
  autoGenerateFilterOrderNumber?: OptionTypeBase;
  autoGenerateFilterOrderNumberId?: string;
  codeTableId?: string;
  codeTable?: OptionTypeBase;
  codeTableMappingId?: string;
  codeTableMapping?: OptionTypeBase;
  lenientOrderControlProcessing?: boolean;
}

export interface IdentifyOrderBy {
  placerOrderNumber: boolean;
  fillerOrderNumber: boolean;
  placerGroupNumber: boolean;
  studyInstanceUid: boolean;
}
export const defaultValueHl7Service: Readonly<HL7Service> = {
  id: "",
  name: "",
  whitelistIps: "",
  enabled: true,
  retentionTimeSuccess: {
    value: 30,
    periodType: "DAYS",
  },
  retentionTimeFailed: {
    value: 30,
    periodType: "DAYS",
  },
  defaultOrganisation: "",
  defaultTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  transforms: [],
  conditionRule: null,
  version: "",
  migration: false,
  allowReportOverride: false,
  identifyOrderBy: {
    placerOrderNumber: true,
    fillerOrderNumber: true,
    placerGroupNumber: true,
    studyInstanceUid: true,
  },
};

export type MessageTypeModel = {
  id?: string;
  name: string;
};

export type TriggerEvents = {
  id?: string;
  name: string;
};

export type DataMapping = {
  id?: string;
  name: string;
  messageType?: string;
  version?: string;
  readonly?: boolean;
};

export type TriggerEventsResponse = {
  code?: string;
  description?: string;
};

export type InterfaceVersionResponse = {
  version?: string;
  value?: string;
};

export type HL7ServiceMessageMappingModel = {
  messageType?: string;
  triggerEvent?: string;
  hl7DataMappingId?: string;
};

export interface CodingSystem {
  id?: string;
  hl7Scheme?: string;
  hl7Version?: string;
  fhirSystem?: string;
  fhirVersion?: string;
}

export const defaultCodingSystem: Readonly<CodingSystem> = {
  id: "",
  hl7Scheme: "",
  hl7Version: "",
  fhirSystem: "",
  fhirVersion: "",
};

export interface HL7OutboundService {
  id?: string;
  name: string;
  mode?: HL7ServiceMode;
  gateway?: DropDownResponse<string>;
  port?: number;
  hostname?: number;
  enabled: boolean;
  retentionTimeSuccess?: RetentionTime;
  retentionTimeFailed?: RetentionTime;
  transforms: any[];
  version?: string;
  type?: HL7OutboundServiceType;
  path?: string;
  serviceUrl?: string;
  hl7Version?: string;
  readTimeout?: number;
  retryAttempts?: number;
  needsAck?: boolean;
  defaultTimezone?: string;
  timezone?: string;
  defaultIssuerOfPatient?: OptionTypeBase;
  defaultIssuerOfPatientId?: string;
  defaultIssuerOfAccessionNumberOption?: OptionTypeBase;
  defaultIssuerOfAccessionNumber?: string;
  sendingFacility?: string;
  receivingFacility?: string;
  receivingApplication?: string;
  codeTableId?: string;
  codeTable?: OptionTypeBase;
  codeMode?: CodeMode;
}

export enum CodeMode {
  SOURCE = "SOURCE",
  MAPPED = "MAPPED",
}

export const defaultValueHl7OutboundService: Readonly<HL7OutboundService> = {
  id: "",
  name: "",
  type: null,
  enabled: true,
  retentionTimeSuccess: {
    value: 30,
    periodType: "DAYS",
  },
  retentionTimeFailed: {
    value: 30,
    periodType: "DAYS",
  },
  defaultTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  transforms: [],
  version: "",
  retryAttempts: 5,
  needsAck: true,
  codeMode: CodeMode.MAPPED,
};

export enum HL7OutboundServiceType {
  GATEWAY = "GATEWAY",
  HTTPS = "HTTPS",
}

export const HL7OutboundInterfaceVersion = {
  TWENTY_TWENTY_FOUR_AUGUST_FIFTEEN: "2024-08-15",
};

export interface IHL7OutboundServiceList {
  name: string;
  gateway: IGateway;
  port: string;
  id: string;
  type?: string;
  status?: string;
}

export const HL7Version = {
  TWO_THREE_ONE: "2.3.1",
  TWO_FIVE_ONE: "2.5.1",
};
